import './datatable.css'
import { EmptyState, SkeletonRows } from './index.jsx'

// Nearly every screen is a table, so it is worth one good component (§10).
//
// One `columns` array drives the colgroup widths, the alignment, the priority
// and the cells. Header and body cannot disagree, because they are generated
// from the same source — CSS keyed by class name, with header and body as
// separate rules, drifts the moment a column is added.
//
// A real <table>, not a CSS grid pretending to be one: position: sticky works
// on th and td, and a grid has no semantics and no column spanning.
//
// Breakpoints, and the correction from §09a: sticky first and last columns are
// right on a desktop and wrong on a tablet, where they eat half the viewport.
//   >= 1080px  all columns, both sticky
//   720-1079   priority 1-2 only, nothing sticky
//   < 720px    the same column array renders as two-line cards
// One component, not two.

export function DataTable({
  columns,
  rows,
  rowKey = 'id',
  sort,
  onSortChange,
  onRowClick,
  loading = false,
  error = null,
  empty,
  caption,
  pagination,
  // Opts out of the bounded inner scrollport datatable.css gives every table
  // by default (see the note on .dt-scroll there). Good default everywhere a
  // table can genuinely run to hundreds of rows and a stuck header earns its
  // keep; wrong for a short, already-paginated list, where it reads as a
  // second, independent scrollbar fighting the page's own. Opt in per screen.
  flowHeight = false,
}) {
  const body = () => {
    // Loading, empty and error all render INSIDE the frame, so the page never
    // reflows between them.
    if (loading) return <SkeletonRows columns={columns} />
    if (error) {
      return (
        <tr>
          <td colSpan={columns.length}>
            <EmptyState title={error} />
          </td>
        </tr>
      )
    }
    if (!rows?.length) {
      return (
        <tr>
          <td colSpan={columns.length}>{empty ?? <EmptyState title="Nothing here yet." />}</td>
        </tr>
      )
    }
    return rows.map((row) => (
      <tr
        key={row[rowKey]}
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        className={onRowClick ? 'dt-clickable' : undefined}
        tabIndex={onRowClick ? 0 : undefined}
        onKeyDown={
          onRowClick
            ? (e) => {
                if (e.key === 'Enter') onRowClick(row)
              }
            : undefined
        }
      >
        {columns.map((c) => (
          <td
            key={c.key}
            className={cellClass(c)}
            data-label={c.header}
            data-priority={c.priority ?? 3}
          >
            {c.cell(row)}
          </td>
        ))}
      </tr>
    ))
  }

  return (
    <div className="dt-frame">
      <div className={flowHeight ? 'dt-scroll dt-scroll-flow' : 'dt-scroll'}>
        <table className="dt">
          {caption ? <caption className="visually-hidden">{caption}</caption> : null}
          {/* One colgroup, driven by the same array that renders the cells. */}
          <colgroup>
            {columns.map((c) => (
              <col key={c.key} style={{ width: c.width }} data-priority={c.priority ?? 3} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={cellClass(c)}
                  data-priority={c.priority ?? 3}
                  aria-sort={ariaSort(c, sort)}
                >
                  {c.sortable && onSortChange ? (
                    <button
                      type="button"
                      className="dt-sort"
                      onClick={() => onSortChange(nextSort(c.key, sort))}
                    >
                      {c.header}
                      <span aria-hidden="true" className="dt-caret">
                        {sortIndicator(c.key, sort)}
                      </span>
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{body()}</tbody>
        </table>
      </div>

      {/* The footer bar lives inside the frame, so it does not float away from
          the rows it describes. */}
      {pagination ? (
        <div className="dt-foot">
          <span className="dt-range">{pagination.label}</span>
          <div className="dt-pager">{pagination.controls}</div>
        </div>
      ) : null}
    </div>
  )
}

function cellClass(column) {
  const classes = []
  // Text left. Numbers right, mono, tabular-nums. No per-column exceptions:
  // centred numbers cannot be compared, and centred text has a ragged left edge.
  if (column.align === 'right') classes.push('dt-right')
  if (column.sticky === 'left') classes.push('dt-sticky-left')
  if (column.sticky === 'right') classes.push('dt-sticky-right')
  return classes.join(' ')
}

function ariaSort(column, sort) {
  if (!column.sortable || !sort) return undefined
  const [field, direction] = sort.split(':')
  if (field !== column.key) return 'none'
  return direction === 'desc' ? 'descending' : 'ascending'
}

function sortIndicator(key, sort) {
  if (!sort) return ''
  const [field, direction] = sort.split(':')
  if (field !== key) return ''
  return direction === 'desc' ? '↓' : '↑'
}

// Sorting is server-side only — never sort the fifty rows on screen under a
// header that implies all 1,284.
function nextSort(key, sort) {
  const [field, direction] = (sort || '').split(':')
  if (field !== key) return `${key}:asc`
  return direction === 'asc' ? `${key}:desc` : `${key}:asc`
}
